import { useState, useEffect } from "react";
import { Loader2, AlertCircle } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { fetchYouTubeData, createTranscriptSections, createTranscriptChunks } from "@/lib/youtube-service";
import { UnifiedTranscript } from "./UnifiedTranscript";
import { YouTubePlayerRef } from "./YouTubeEmbed";

interface TranscriptItem {
  text: string;
  start: number;
  duration: number;
}

interface TranscriptSection {
  title: string;
  timestamp: string;
  description: string;
  startTime: number;
}

interface YouTubeChapter {
  title: string;
  start_time: number;
  timestamp: string;
}

interface YouTubeTranscriptProps {
  videoId: string;
  title: string;
  storedTranscript?: TranscriptItem[];
  storedChapters?: YouTubeChapter[];
  hasStoredChapters?: boolean;
  playerRef?: React.RefObject<YouTubePlayerRef>;
}

interface TranscriptResponse {
  transcript: TranscriptItem[];
  chapters: YouTubeChapter[];
  has_chapters: boolean;
  video_id: string;
  language: string;
  full_text: string;
  total_segments: number;
}

// Removed server-side fetchTranscript function - now using client-side fetchYouTubeData directly

const formatTime = (seconds: number): string => {
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = Math.floor(seconds % 60);
  return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
};

// createTranscriptSections function moved to @/lib/youtube-service

export const YouTubeTranscript = ({ videoId, title, storedTranscript, storedChapters, hasStoredChapters, playerRef }: YouTubeTranscriptProps) => {
  const [transcript, setTranscript] = useState<TranscriptItem[]>([]);
  const [chapters, setChapters] = useState<YouTubeChapter[]>([]);
  const [sections, setSections] = useState<TranscriptSection[]>([]);
  const [hasChapters, setHasChapters] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const { toast } = useToast();

  useEffect(() => {
    const loadTranscript = async () => {
      setIsLoading(true);
      setError(null);
      
      try {
        // Use stored data if available, otherwise fetch from API
        if (storedTranscript && storedTranscript.length > 0) {
          console.log('📄 Using stored transcript data for video:', videoId);
          
          // Apply 30-second chunking to stored transcript for consistency
          const chunkedTranscript = createTranscriptChunks(storedTranscript);
          console.log(`📝 Processed stored transcript into ${chunkedTranscript.length} chunks (30s each) from ${storedTranscript.length} raw segments`);
          
          setTranscript(chunkedTranscript);
          setChapters(storedChapters || []);
          setHasChapters(hasStoredChapters || false);
          
          // Create sections from stored data
          let transcriptSections: TranscriptSection[] = [];
          
          if (hasStoredChapters && storedChapters && storedChapters.length > 0) {
            // Convert chapters to sections format for compatibility
            const chapterSections: TranscriptSection[] = storedChapters.map(chapter => ({
              title: chapter.title,
              timestamp: chapter.timestamp,
              description: '', // YouTube chapters don't have descriptions
              startTime: chapter.start_time
            }));
            setSections(chapterSections);
          } else {
            const generatedChapters = createTranscriptSections(chunkedTranscript);
            transcriptSections = generatedChapters.map(chapter => ({
              title: chapter.title,
              timestamp: chapter.timestamp,
              description: '', // Generated sections don't have descriptions
              startTime: chapter.start_time
            }));
            setSections(transcriptSections);
          }
          
          // Tab state no longer needed - using unified transcript view
        } else {
          console.log('🌐 Fetching transcript data from client-side API for video:', videoId);
          
          try {
            // Use client-side YouTube service instead of server API
            const response = await fetchYouTubeData(videoId);
            
            // Check if we actually got any data
            if (!response.transcript || response.transcript.length === 0) {
              console.warn(`⚠️ No transcript available for video ${videoId}`);
              setError('This video does not have transcripts available. This could be because:\n• Subtitles are disabled for this video\n• The video is private or restricted\n• Transcripts are not available in the requested language');
              setTranscript([]);
              setChapters([]);
              setHasChapters(false);
              setSections([]);
              return;
            }
            
            setTranscript(response.transcript);
            setChapters(response.chapters);
            setHasChapters(response.has_chapters);
            
            // Create sections from transcript as fallback
            let transcriptSections: TranscriptSection[] = [];
            
            if (response.has_chapters && response.chapters.length > 0) {
              // Convert chapters to sections format for compatibility
              const chapterSections: TranscriptSection[] = response.chapters.map(chapter => ({
                title: chapter.title,
                timestamp: chapter.timestamp,
                description: '', // YouTube chapters don't have descriptions
                startTime: chapter.start_time
              }));
              setSections(chapterSections);
            } else {
              const generatedChapters = createTranscriptSections(response.transcript);
              transcriptSections = generatedChapters.map(chapter => ({
                title: chapter.title,
                timestamp: chapter.timestamp,
                description: '', // Generated sections don't have descriptions
                startTime: chapter.start_time
              }));
              setSections(transcriptSections);
            }
            
            // Tab state no longer needed - using unified transcript view
            
            console.log(`✅ Successfully loaded transcript with ${response.transcript.length} items for video ${videoId}`);
            
          } catch (fetchError) {
            console.error('Error fetching YouTube data:', fetchError);
            const errorMessage = fetchError instanceof Error ? fetchError.message : 'Unknown error';
            
            if (errorMessage.includes('No transcript available')) {
              setError('This video does not have transcripts available. This could be because:\n• Subtitles are disabled for this video\n• The video is private or restricted\n• Auto-generated captions are not available');
            } else if (errorMessage.includes('CORS')) {
              setError('Unable to fetch transcript due to browser restrictions. The video may still be playable.');
            } else {
              setError(`Failed to load transcript: ${errorMessage}`);
            }
            
            // Set empty state
            setTranscript([]);
            setChapters([]);
            setHasChapters(false);
            setSections([]);
          }
        }
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to load transcript';
        
        // Enhanced error handling for client-side issues
        if (errorMessage.includes('CORS') || errorMessage.includes('blocked')) {
          setError('Unable to access video transcript due to browser security restrictions. Try opening the video directly on YouTube.');
        } else if (errorMessage.includes('not available') || errorMessage.includes('no transcript')) {
          setError('This video does not have captions available or they may be disabled by the creator.');
        } else if (errorMessage.includes('Network error')) {
          setError('Network error: Please check your internet connection and try again.');
        } else {
          setError('Failed to load transcript. Please try again later.');
        }
      } finally {
        setIsLoading(false);
      }
    };

    loadTranscript();
  }, [videoId, storedTranscript, storedChapters, hasStoredChapters]);

  const copyFullTranscript = async () => {
    try {
      const fullText = transcript.map(item => item.text).join(' ');
      await navigator.clipboard.writeText(fullText);
      
      toast({
        title: "Copied!",
        description: "Full transcript copied to clipboard"
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to copy transcript to clipboard",
        variant: "destructive"
      });
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-32">
        <Loader2 className="w-6 h-6 animate-spin text-slate-600 mr-2" />
        <span className="text-sm text-slate-600">Loading transcript...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-32 text-center">
        <AlertCircle className="w-8 h-8 text-amber-500 mb-2" />
        <h3 className="text-base font-medium text-slate-900 mb-1">Transcript unavailable</h3>
        <p className="text-sm text-slate-600">
          This video may not have captions available, or they may be disabled by the creator.
        </p>
      </div>
    );
  }

  return (
    <div className="h-full">
      <UnifiedTranscript
        transcript={transcript}
        chapters={chapters}
        sections={sections}
        hasChapters={hasChapters}
        playerRef={playerRef}
      />
    </div>
  );
};